// The confidence cells the two lead boards share. `/leads` and `/leads/search`
// render the same row, so the badge, its colour bands, the hover text behind it
// and the numbers it shows all have to agree - and every one of them was a
// second copy carrying a "mirrors the other page" comment.

// Min of (1) the geocoder's own self-assessment and (2) BMA's polygon-vs-VOA
// match score, on the same weakest-link principle as `leadConfidence` (min of
// polygon, landlord, tenant). With only one signal present we use it rather
// than nulling the row - the badge tooltip names which side was missing.
export const combinedBuildingConfidence = (
  locationConfidence: number | null,
  buildingConfidence: number | null
): number | null => {
  if (locationConfidence == null && buildingConfidence == null) return null;
  if (locationConfidence == null) return buildingConfidence;
  if (buildingConfidence == null) return locationConfidence;
  return Math.min(locationConfidence, buildingConfidence);
};

// A geocoder confidence label as a 0-100 percent, so the Location column can
// render in the same shape as the Building / Tenant / Landlord cells beside it.
const LOCATION_CONFIDENCE_BY_LABEL: Record<string, number> = {
  HIGH: 95,
  MEDIUM: 70,
  LOW: 35,
  POSTCODE_CENTROID: 15,
};

export const locationConfidenceForLabel = (
  label: string | null | undefined
): number | null => {
  if (!label) return null;
  return LOCATION_CONFIDENCE_BY_LABEL[label.toUpperCase()] ?? null;
};

// Hover text for the Tenant Conf badge. Per-source, so the operator can see
// whether the score is driven by CH SIC matching, OSM proximity or Places.
export const buildTenantTooltip = (
  match: { source?: string | null; confidence: number | null } | null
): string => {
  if (!match) return "No tenant match.";
  const conf =
    match.confidence == null ? "—" : `${Math.round(match.confidence)}%`;
  const sourceLine = (() => {
    switch (match.source) {
      case "ch":
        return "Companies House: postcode-keyed search; ranked by SIC-code match, active status, address fuzziness.";
      case "osm":
        return "OpenStreetMap: tagged-business proximity to the building polygon.";
      case "google_places":
        return "Google Places: nearby-search around the building; ranked by distance + name similarity.";
      default:
        return "Tenant match from upstream pipeline.";
    }
  })();
  return `Tenant: ${conf}\n${sourceLine}`;
};

// Hover text for the Landlord Conf badge. HMLR-only; explains the distance +
// polygon-overlap basis.
export const buildLandlordTooltip = (
  match: { confidence: number | null; distanceMetres?: number | null } | null
): string => {
  if (!match) return "No landlord match.";
  const conf =
    match.confidence == null ? "—" : `${Math.round(match.confidence)}%`;
  const distLine =
    match.distanceMetres != null
      ? `Deed centroid ${Math.round(match.distanceMetres)}m from building polygon.`
      : null;
  return [
    `Landlord: ${conf}`,
    "HMLR title deed, polygon-filtered to deeds overlapping the selected building.",
    distLine,
  ]
    .filter(Boolean)
    .join("\n");
};
